import { useState, useEffect, useMemo, useRef } from 'react';
import {
  PREDEFINED_VIEWS,
  loadUserViews,
  addUserView,
  deleteUserView,
  setLastViewName,
  filtersEqual,
  countViewMatches,
} from '../../contexts/inventorySavedViews';
import { applyAxisFilters } from '../../contexts/inventoryFilterAxes';
import { useInventory } from '../../contexts/InventoryContext';

/**
 * SavedViewTabs — tab strip above the filter rail.
 *
 * Predefined views render first (undeletable), then user-saved.
 * Active tab = the one whose chip set matches current `filters[]`
 * exactly. When nothing matches, an inline "+ Save view" affordance
 * lets the user name the current chip set into a new view.
 *
 * Props:
 *   caseId         — for localStorage scoping
 *   filters        — current active filters[]
 *   docsForFacets  — pre-axis-chip doc set; used for live tab counts
 *   ctx            — passed through to applyAxisFilters
 *   onLoadView     — (filters, columns?) => void; dispatches SET_FILTERS + SET_COLUMNS
 */
export default function SavedViewTabs({ caseId, unit = null, filters, docsForFacets, ctx, onLoadView }) {
  const { visibleColumns } = useInventory();
  const [userState, setUserState] = useState(() => loadUserViews(caseId));
  const [savingMode, setSavingMode] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState(null);
  const inputRef = useRef(null);

  // Reload from storage when case changes
  useEffect(() => {
    setUserState(loadUserViews(caseId));
    setSavingMode(false);
    setSaveName('');
    setSaveError(null);
  }, [caseId]);

  // Autofocus the save-name input
  useEffect(() => {
    if (savingMode) inputRef.current?.focus();
  }, [savingMode]);

  const allViews = useMemo(
    () => [...PREDEFINED_VIEWS, ...(userState.views || [])],
    [userState]
  );
  const activeView = useMemo(
    () => allViews.find(v => filtersEqual(v.filters, filters)) || null,
    [allViews, filters]
  );

  function handleClick(view) {
    onLoadView?.(view.filters, view.columns, view.unit || null);
    setLastViewName(caseId, view.name);
    setUserState(loadUserViews(caseId));
  }

  function handleDelete(viewId, e) {
    e.stopPropagation();
    if (!window.confirm('Delete this view?')) return;
    deleteUserView(caseId, viewId);
    setUserState(loadUserViews(caseId));
  }

  function handleSave() {
    const view = addUserView(caseId, saveName, filters, visibleColumns, unit);
    if (!view) {
      setSaveError('Name already exists or is empty');
      return;
    }
    setLastViewName(caseId, view.name);
    setUserState(loadUserViews(caseId));
    setSavingMode(false);
    setSaveName('');
    setSaveError(null);
  }

  // Only offer "+ Save view" when there's something to save AND it
  // doesn't already match a view (predefined or user-saved).
  const canSave = filters.length > 0 && !activeView;

  return (
    <div className="saved-view-tabs" role="tablist">
      {allViews.map(view => {
        const isActive = activeView?.id === view.id;
        const count = countViewMatches(view, docsForFacets, applyAxisFilters, ctx);
        return (
          <button
            key={view.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={[
              'saved-view-tab',
              isActive ? 'saved-view-tab--active' : '',
              view.system ? 'saved-view-tab--system' : 'saved-view-tab--user',
            ].filter(Boolean).join(' ')}
            onClick={() => handleClick(view)}
          >
            <span className="saved-view-tab-name">{view.name}</span>
            <span className="saved-view-tab-count">{count}</span>
            {!view.system && (
              <span
                className="saved-view-tab-x"
                role="button"
                tabIndex={0}
                onClick={(e) => handleDelete(view.id, e)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDelete(view.id, e); }}
                aria-label={`Delete view ${view.name}`}
              >
                ✕
              </span>
            )}
          </button>
        );
      })}

      {savingMode ? (
        <span className="saved-view-save-inline">
          <input
            ref={inputRef}
            type="text"
            className="saved-view-save-input"
            placeholder="Name this view…"
            value={saveName}
            onChange={(e) => { setSaveName(e.target.value); setSaveError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              else if (e.key === 'Escape') {
                setSavingMode(false);
                setSaveName('');
                setSaveError(null);
              }
            }}
          />
          <button type="button" className="saved-view-save-btn" onClick={handleSave}>Save</button>
          <button
            type="button"
            className="saved-view-save-cancel"
            onClick={() => { setSavingMode(false); setSaveName(''); setSaveError(null); }}
          >
            Cancel
          </button>
          {saveError && <span className="saved-view-save-error">{saveError}</span>}
        </span>
      ) : (
        canSave && (
          <button
            type="button"
            className="saved-view-tab saved-view-tab--add"
            onClick={() => setSavingMode(true)}
            title="Save current filters as a view"
          >
            + Save view
          </button>
        )
      )}
    </div>
  );
}
